import React, { useState, useRef, useCallback } from "react"
import { nanoid } from "nanoid"
import cx from "classnames"
import { IconProps, StarIcon } from "../Icon"

export type RatingIconsPropsType = {
  className?: string
  max?: 5 | 10
  value: number
  size?: IconProps["size"] | 48
  editable?: boolean
  disabled?: boolean
  onChange?: (newValue: number) => void
  icon?: JSX.Element | JSX.Element[]
}

export type RatingIconPropsType = {
  icon: JSX.Element
  status?: "full" | "half" | "empty"
  size?: RatingIconsPropsType["size"]
}

export const defaultRatingIcon = <StarIcon color="infoAlt" />
const itemEmptyColor: IconProps["color"] = "disabled"

export function roundHalf(num: number) {
  const temp = num * 2
  return temp % 1 === 0.5 ? Math.floor(temp) / 2 : Math.round(temp) / 2
}

export function computeItemStatus(
  ratingValue: number,
  itemValue: number,
  icon: RatingIconsPropsType["icon"]
): RatingIconPropsType["status"] {
  let itemStatus: RatingIconPropsType["status"] = "empty"
  if (!Array.isArray(icon)) {
    // gauge
    const isHalfItem = itemValue > ratingValue && ratingValue > itemValue - 1
    if (isHalfItem) {
      itemStatus = "half"
    } else if (itemValue <= ratingValue) {
      itemStatus = "full"
    }
  } else if (itemValue === ratingValue) {
    // radio
    itemStatus = "full"
  }

  return itemStatus
}

export const RatingIcon = ({
  icon,
  status = "full",
  size = 24,
}: RatingIconPropsType) => {
  if (status === "half") {
    return (
      <>
        {React.cloneElement(icon, {
          color: itemEmptyColor,
          size,
        })}
        <div className="cobalt-rating-icons__icon-half-container">
          {React.cloneElement(icon, { size })}
        </div>
      </>
    )
  } else {
    const iconColor: IconProps["color"] =
      status === "empty" ? itemEmptyColor : icon.props.color

    return React.cloneElement(icon, {
      color: iconColor,
      size,
    })
  }
}

export const RatingIcons = ({
  className,
  max = 5,
  value,
  size = 24,
  editable = false,
  onChange,
  icon = defaultRatingIcon,
}: RatingIconsPropsType) => {
  const name = useRef(nanoid())

  const items = useRef(
    Array.from(new Array(Array.isArray(icon) ? icon.length : max))
  )

  const [ratingValue, setRatingValue] = useState(() =>
    editable ? Math.floor(value) : roundHalf(value)
  )
  const [selectedIndex, setSelectedIndex] = useState(-1) // used to enforce selection effect (icon scale reset)

  const resetSelection = useCallback(() => {
    setSelectedIndex(-1)
  }, [])

  const itemSize = size === 48 ? 16 : size

  // input element is used for a11y purpose ( focus feedback and keyboard selection )
  return (
    <div
      className={cx("cobalt-rating-icons", className, {
        "cobalt-rating-icons--icon-size-48": size === 48,
      })}
      onPointerLeave={() => {
        if (editable) {
          ;(document.activeElement as HTMLInputElement).blur()
        }
      }}
    >
      {items.current.map((_value, idx) => {
        const itemValue = idx + 1

        const itemStatus = computeItemStatus(ratingValue, itemValue, icon)

        return (
          <label
            className={cx("cobalt-rating-icons__icon-wrapper", {
              "cobalt-rating-icons__icon-wrapper--selected":
                selectedIndex === idx,
            })}
            key={idx}
            onClick={() => {
              if (editable) {
                setRatingValue(itemValue)
                setSelectedIndex(idx)
              }
            }}
            onMouseEnter={(e) => {
              if (editable) {
                ;(e.target as HTMLElement).focus()

                resetSelection()
              }
            }}
          >
            <input
              tabIndex={
                editable &&
                (ratingValue === itemValue ||
                  (ratingValue === 0 && itemValue === 1))
                  ? 0
                  : -1
              }
              name={name.current}
              type="radio"
              value={itemValue}
              onChange={() => {
                onChange && onChange(itemValue)
              }}
              disabled={!editable}
            />
            {
              <RatingIcon
                icon={Array.isArray(icon) ? icon[idx] : icon}
                status={itemStatus}
                size={itemSize}
              />
            }
          </label>
        )
      })}
    </div>
  )
}
